import { EnumButtonName, EnumDialogName } from '@calm/evexia-analytics';
import commonMessages from '@rhg/lang/messages/common';
import { Stack, Typography } from '@rhg/libs/material';
import { WarningOutline } from '@rhg/libs/material/icons';
import { DialogButton } from '@rhg/libs/material/simple-dialog/types';
import { calmHealthSupportEmail } from '@rhg/utils/constants';
import { ComponentProps, useCallback, useId } from 'react';
import { useIntl } from 'react-intl';
import { SheetDialog } from '../../sheet-dialog';

type SheetDialogProps = Omit<ComponentProps<typeof SheetDialog>, 'content'>;

export function GeneralErrorDialog({
    errorContent,
    onOpen,
    onClose,
    trackConfirm = 'ErrorConfirm',
    trackCloseButton,
    trackDialog = 'ServerError',
    trackFeature,
    title,
    trackScreen,
    showSupportEmail = false,
    showSupportMessage = false,
    supportEmail,
    'aria-label': ariaLabel,
    buttons: customButtons,
}: {
    onClose: () => void;
    showSupportEmail?: boolean;
    showSupportMessage?: boolean;
    errorContent?: string;
    trackConfirm?: EnumButtonName;
    supportEmail?: string;
    'aria-label'?: string;
    trackDialog?: EnumDialogName;
} & Omit<SheetDialogProps, 'trackDialog'>) {
    const { formatMessage } = useIntl();
    const id = useId();

    const errorTitle =
        title ??
        formatMessage(commonMessages['common.error.error_dialog.title']);
    const errorText = errorContent
        ? errorContent
        : showSupportMessage
        ? formatMessage(
              commonMessages['common.error.error_dialog.description_support']
          )
        : formatMessage(
              commonMessages['common.error.error_dialog.description']
          );

    const buttons = useCallback(
        (): DialogButton[] => [
            showSupportEmail
                ? {
                      trackButton: 'ContactSupport',
                      variant: 'text',
                      href: `mailto:${supportEmail ?? calmHealthSupportEmail}`,
                      color: 'primary',
                      title: supportEmail ?? calmHealthSupportEmail,
                  }
                : {
                      trackButton: trackConfirm,
                      variant: 'contained',
                      onClick: () => onClose(),
                      title: formatMessage(
                          commonMessages[
                              'common.error.error_dialog.button_text'
                          ]
                      ),
                  },
        ],
        [showSupportEmail, supportEmail, trackConfirm, onClose, formatMessage]
    );

    return (
        <SheetDialog
            aria-labelledby={id}
            aria-label={ariaLabel}
            trackCloseButton={trackCloseButton}
            trackDialog={trackDialog}
            title={errorTitle}
            trackScreen={trackScreen}
            trackFeature={trackFeature}
            onClose={onClose}
            onOpen={onOpen}
            buttons={customButtons ? customButtons : buttons()}
            icon={<WarningOutline color="warning" />}
            content={
                <Stack direction="column" gap={2}>
                    <Typography variant="srOnly" id={id}>
                        {title}
                    </Typography>
                    <Typography
                        variant="body1"
                        color="text.body"
                        align="center"
                    >
                        {errorText}
                    </Typography>
                </Stack>
            }
        />
    );
}
